<template>
  <v-container
    class="rounded elevation-3"
    style="background-color: white;"
    fluid
  >
    <v-row
      class="pa-4"
    >
      <v-col cols="6">
        <h2>Health Check</h2>
      </v-col>
      <v-col
        cols="6"
        class="d-flex justify-end">
        <div>
          <v-select
            v-model="selectedMonth"
            :items="monthOptions"
            item-text="text"
            item-value="value"
            label="Select Month"
            outlined
            dense
            :menu-props="{
              auto: true,
              offsetY: true
            }"
          />
        </div>
      </v-col>
    </v-row>

    <v-card-text>
      <div style="overflow-y: auto;">
        <table
          v-if="!loading"
          class="table">
          <thead
            v-if="collections.length"
          >
            <tr>
              <th
                v-for="header in headers"
                :key="header.value"
                :class="header.classCss"
                class="text-center"
                :style="`min-width: ${header.width}px; white-space: nowrap;`"
              >
                {{ header.text }}
              </th>
            </tr>
          </thead>
          <tbody v-if="collections.length">
            <tr
              v-for="collection in collections"
              :key="collection.id">
              <td
                align="center"
                class="sticky-col col-1">
                <div class="d-flex flex-column align-center text-caption">
                  <h3 class="text-h6">
                    {{ collection.name }}
                  </h3>
                  <h3 class="text-h6">
                    {{ $dayjs(collection.salesDate).format('DD/MM/YYYY') }}
                  </h3>
                </div>
              </td>
              <td align="center">
                {{ collection.currentStockValue | showNumberFormat }} / {{ collection.targetStockValue | showNumberFormat }}
              </td>
              <td align="center">
                <table class="tableDeliveryStatus">
                  <thead>
                    <tr>
                      <th
                        v-for="header in deliveryStatusHeaders"
                        :key="header.value"
                        :class="header.classCss"
                        class="text-center"
                        :style="`min-width: ${header.width}px; white-space: nowrap;`"
                      >
                        {{ header.text }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, index) in Object.values(collection.deliveryStatusPercent)"
                      :key="index"
                      align="center"
                    >
                      <td :class="item.style">
                        {{ item.text }}
                      </td>
                      <td>{{ item.percent }}%</td>
                      <td>{{ item.stockValue | showNumberFormat }}</td>
                      <td>{{ item.stockQty }}</td>
                    </tr>
                  </tbody>
                </table>
              </td>
              <td
                v-for="stage in workflow.stages"
                :key="stage.id"
              >
                <div
                  v-if="collection.worstStatusOfStage[stage.id]"
                  class="task-status text-caption ma-1 rounded"
                  :class="collection.worstStatusOfStage[stage.id].bgClass"
                >
                  <v-row
                    no-gutters
                    class="task-status">
                    <v-col
                      cols="2"
                      class="d-flex align-center justify-center"
                    >
                      <v-icon
                        v-if="collection.worstStatusOfStage[stage.id].status === 'Completed'"
                        color="green">
                        mdi-check-circle
                      </v-icon>
                    </v-col>
                    <v-col
                      class="d-flex flex-column justify-center">
                      <p
                        class="font-weight-bold text-h6 mb-0"
                        :class="collection.worstStatusOfStage[stage.id].textColor1"
                      >
                        {{ collection.worstStatusOfStage[stage.id].status }}
                      </p>
                      <span
                        v-if="
                          collection.worstStatusOfStage[stage.id].status !== 'Completed'
                            && collection.worstStatusOfStage[stage.id].status !== 'No Task'"
                        class="text-body-1"
                        :class="collection.worstStatusOfStage[stage.id].textColor1"
                      >
                        Incomplete: {{
                          collection.worstStatusOfStage[stage.id].incompleteCount
                        }}
                      </span>
                    </v-col>
                  </v-row>
                </div>
                <div
                  v-else
                  class="task-status d-flex align-center justify-center">
                  {{ stage.id === 0 ? 'ไม่มีข้อมูล Stage' : 'NO TASK' }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="isWorkflowNull"
        class="pa-4 text-center">
        กรุณาสร้าง Workflow ก่อนดำเนินการต่อ
      </div>
      <div
        v-if="loading"
        class="tableContainer d-flex white justify-center align-center"
      >
        <v-progress-circular
          indeterminate
          size="24" />
      </div>
      <div
        v-if="!loading && collections.length === 0 && !isWorkflowNull"
        class="pa-4 text-center">
        ไม่พบข้อมูล Collection
      </div>
    </v-card-text>
  </v-container>
</template>

<script>
import calculateTaskStatus from '@/assets/js/CalculateTaskStatus'
import ProductionPlanProvider from '@/resources/ProductionPlanProvider'
import { mapActions } from 'vuex/dist/vuex.common'

const ProductionPlanService = new ProductionPlanProvider()

export default {
  data () {
    return {
      loading: false,
      selectedMonth: this.$dayjs().format('MM-YYYY'),
      status: {
        critical: {
          key: 'critical',
          status: 'Critical',
          taskStatusOnComplete: 'completed_critical',
          extendText: '',
          textColor1: 'white--text',
          textColor2: 'orange--text',
          bgClass: 'red darken-4'
        },

        overdue: {
          key: 'overdue',
          status: 'Overdue',
          taskStatusOnComplete: 'completed_overdue',
          extendText: '',
          textColor1: 'secondary--text',
          textColor2: 'red--text',
          bgClass: 'red lighten-4'
        },
        dueIn0: {
          key: 'dueIn0',
          status: 'Due Soon',
          taskStatusOnComplete: 'completed_on_time',
          extendText: '',
          textColor1: 'secondary--text',
          textColor2: 'orange--text',
          bgClass: 'yellow lighten-4'
        },
        dueIn1: {
          key: 'dueIn1',
          status: 'Due Soon',
          taskStatusOnComplete: 'completed_on_time',
          extendText: '',
          textColor1: 'secondary--text',
          textColor2: 'orange--text',
          bgClass: 'yellow lighten-4'
        },
        dueIn2: {
          key: 'dueIn2',
          status: 'Due Soon',
          taskStatusOnComplete: 'completed_on_time',
          extendText: '',
          textColor1: 'secondary--text',
          textColor2: 'orange--text',
          bgClass: 'yellow lighten-4'
        },

        inProgress: {
          key: 'inProgress',
          status: 'In Progress',
          taskStatusOnComplete: 'completed_on_time',
          extendText: '',
          textColor1: 'secondary--text',
          textColor2: 'secondary--text',
          bgClass: 'indigo lighten-4'
        },
        pending: {
          key: 'pending',
          status: 'Pending',
          taskStatusOnComplete: 'completed_on_time',
          extendText: '',
          textColor1: 'secondary--text',
          textColor2: 'secondary--text',
          bgClass: 'grey lighten-3'
        },

        completed_on_time: {
          key: 'completed_on_time',
          status: 'Completed',
          taskStatusOnComplete: '',
          extendText: 'On Time',
          textColor1: 'secondary--text',
          textColor2: 'green--text',
          bgClass: 'green lighten-4'
        },
        completed_overdue: {
          key: 'completed_overdue',
          status: 'Completed',
          taskStatusOnComplete: '',
          extendText: 'Overdue',
          textColor1: 'secondary--text',
          textColor2: 'orange--text text--darken-2',
          bgClass: 'green lighten-4'
        },
        completed_critical: {
          key: 'completed_critical',
          status: 'Completed',
          taskStatusOnComplete: '',
          extendText: 'Critical',
          textColor1: 'secondary--text',
          textColor2: 'red--text',
          bgClass: 'green lighten-4'
        },
        no_task: {
          key: 'no_task',
          status: 'No Task',
          taskStatusOnComplete: '',
          extendText: '',
          textColor1: 'secondary--text',
          textColor2: 'secondary--text',
          bgClass: 'grey lighten-3'
        }
      },
      collections: [],
      workflow: null,
      headers: [
        {
          text: 'Collection',
          align: 'center',
          width: 300,
          classCss: 'sticky-col col-1'
        },
        {
          text: 'Stock Value',
          align: 'center',
          width: 150,
          classCss: 'col-2'
        },
        {
          text: 'Delivery Status',
          align: 'center',
          width: 300,
          classCss: 'col-3'
        }
      ],
      deliveryStatusHeaders: [
        {
          text: 'Status',
          align: 'center',
          width: 100
        },
        {
          text: 'Percent',
          align: 'center',
          width: 80
        },
        {
          text: 'Stock Value',
          align: 'center',
          width: 100
        },
        {
          text: 'Stock QTY',
          align: 'center',
          width: 80
        }
      ],
      prototypes: [],
      statusPriority: {
        critical: 1,
        overdue: 2,
        dueIn0: 3,
        dueIn1: 4,
        dueIn2: 5,
        inProgress: 6,
        pending: 7,

        completed_critical: 8,
        completed_overdue: 9,
        completed_on_time: 10,
        no_task: 11
      },
      completeStatus: [
        'completed_on_time',
        'completed_overdue',
        'completed_critical'
      ],
      isWorkflowNull: false
    }
  },
  computed: {
    monthOptions () {
      const base = this.$dayjs()
      const months = []

      for (let i = -11; i <= 11; i++) {
        const d = base.add(i, 'month')

        months.push({
          text: d.format('MMM YYYY').toUpperCase(),
          value: d.format('MM-YYYY')
        })
      }
      return months
    }
  },
  watch: {
    selectedMonth () {
      this.getCollectionsWithPrototype()
    }
  },
  async mounted () {
    await this.getWorkFlow()

    if (this.isWorkflowNull) {
      this.loading = false
      return
    }

    await this.getCollectionsWithPrototype()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage',
      setModal: 'Components/setModal'
    }),
    formatTitle (text) {
      return text
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    async getWorkFlow () {
      try {
        const { data } = await ProductionPlanService.getWorkflow()

        if (!data) {
          this.isWorkflowNull = true
          this.workflow = null
          return
        }

        if (data) {
          this.workflow = data
          this.isWorkflowNull = false
          if (!this.workflow?.stages?.length) {
            this.workflow.stages.push({
              id: 0,
              name: 'STAGE',
              pic: 'NO PIC',
              status: 'active',
              order: 0
            })
          }
          this.workflow.stages = data.stages.filter((stage) => stage.status === 'active') ?? []
          this.workflow.stages
            .sort((stageA, stageB) => (stageA.order - stageB.order))
            .forEach((stage, index) => {
              this.headers.push({
                text: `${stage.name} (${this.formatTitle(stage.pic === 'creative' ? 'Creative / Stylist' : stage.pic)})`,
                value: `masterStage${index}`,
                align: 'center',
                width: 250
              })
            })
        }
      } catch (error) {
        console.error(error)
        this.setSnackbar({
          value: true,
          color: 'error',
          message: `Error: ${error.message}`
        })
      }
    },
    getDeliveryStatusStats (data = []) {
      const total = data.length

      const stats = {
        on_time: { text: 'On Time', style: 'success--text', count: 0, stockValue: 0, stockQty: 0 },
        within_month: { text: 'Within Month', style: 'orange--text', count: 0, stockValue: 0, stockQty: 0 },
        late: { text: 'Late', style: 'red--text', count: 0, stockValue: 0, stockQty: 0 },
        no_status: { text: 'ไม่ระบุ', style: '', count: 0, stockValue: 0, stockQty: 0 }
      }

      data.forEach((item) => {
        const status = item.deliveryStatus || 'no_status'
        const key = stats[status] ? status : 'no_status'

        const sellPrice = item?.order?.sellPrice || 0
        const stockQty = item?.order?.colors?.reduce((sum, c) => sum + (c?.totalQty || 0), 0) || 0

        const stockValue = sellPrice * stockQty

        stats[key].count++
        stats[key].stockValue += stockValue
        stats[key].stockQty += stockQty
      })

      return Object.fromEntries(
        Object.entries(stats).map(([key, val]) => [
          key,
          {
            ...val,
            percent: total ? ((val.count / total) * 100).toFixed(2) : '0.00',
            stockValue: val.stockValue,
            stockQty: val.stockQty
          }
        ])
      )
    },
    async getCollectionsWithPrototype () {
      try {
        this.loading = true
        const [month, year] = this.selectedMonth.split('-')
        const { data } = await ProductionPlanService.getCollectionsOverview({
          year: Number(year),
          month: Number(month)
        })
        if (data) {
          this.collections = data.map((collection) => {
            const prototypes = collection.prototype || []
            const allTasks = prototypes.flatMap((p) => p.tasks || [])
            const groupedByStage = allTasks.reduce((acc, task) => {
              if (!task) return acc
              acc[task.stageId] ||= []
              acc[task.stageId].push(task)
              return acc
            }, {})

            const worstStatusOfStage = Object.fromEntries(
              Object.entries(groupedByStage).map(([stageId, tasks]) => {
                const statuses = tasks
                  .filter(Boolean)
                  .map((task) => calculateTaskStatus(task, this.status))

                const worstStatus = statuses.length
                  ? statuses.reduce((worst, current) => (this.statusPriority[current.key] < this.statusPriority[worst.key]
                        ? current
                        : worst))
                  : this.status.no_task

                const incompleteCount = statuses.filter(
                  (s) => !this.completeStatus.includes(s.key)
                ).length

                return [
                  stageId,
                  {
                    ...worstStatus,
                    incompleteCount
                  }
                ]
              })
            )

            const deliveryStatusPercent = this.getDeliveryStatusStats(prototypes)

            return {
              id: collection.id,
              name: collection.name,
              salesDate: collection.salesDate,
              worstStatusOfStage: Object.keys(worstStatusOfStage).length ? worstStatusOfStage : this.status.no_task,
              currentStockValue: prototypes.reduce((sum, p) => sum + (p?.order?.sellPrice || 0)
              * (p?.order?.colors?.reduce((s, n) => s + (n?.totalQty || 0), 0) || 0), 0),
              targetStockValue: collection.targetStockValue || 0,
              deliveryStatusPercent
            }
          })
        }
      } catch (error) {
        console.error(error)
        this.setSnackbar({
          value: true,
          color: 'error',
          message: `Error: ${error.message}`
        })
      } finally {
        this.loading = false
      }
    },
    getWorstStatus (tasks = []) {
      const status = tasks
        .filter(Boolean)
        .map((task) => calculateTaskStatus(task, this.status))

      if (!status.length) return this.status.no_task

      return status.reduce((worst, current) => (this.statusPriority[current.key] < this.statusPriority[worst.key] ? current : worst))
    },
    getDisplayStatus (worstStatus) {
      return this.status[worstStatus.key]
    }
  }
}
</script>

<style scoped>
.v-card__subtitle, .v-card__text, .v-card__title {
  padding-top: 0px !important;
  padding-bottom: 16px !important;
  padding-inline: 16px !important;
}

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.table th {
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  vertical-align: middle;

  width: 250px;
  height: 50px;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table td {
  border: 1px solid #e0e0e0;
  vertical-align: middle;

  width: 250px;
  /* height: 85px; */
}

.sticky-col {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
}

.task-status {
  height: 100%;
  min-height: 135px;
  display: flex;
  align-items: center;
}

.tableContainer {
  width: 100%;
  height: 100%;
}

.tableDeliveryStatus {
  width: 100%;
  border-spacing: 0;
}

.tableDeliveryStatus th {
  border-width: 0 1px 0 0;
  border-style: solid;
  border-color: #e0e0e0;
}

.tableDeliveryStatus td {
  border-width: 1px 1px 0 0;
  border-style: solid;
  border-color: #e0e0e0;
}
</style>
